import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'
import DarkAuthInput from '../../Components/Inputs/DarkAuthInput'
import logo from '../../Assets/new-logo.png'
import { Shield, Check, X } from 'react-feather'

const ConfirmResetPassword = () => {
  const { confirmResetPasswrd, resetError } = useAuth()

  const [accessCode, setAccessCode] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [newVerify, setNewVerify] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showVerifyPassword, setShowVerifyPassword] = useState<boolean>(false)

  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [matchingVerify, setMatchingVerify] = useState<boolean>(false)

  // Password requirements
  const [hasMinLength, setHasMinLength] = useState<boolean>(false)
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false)
  const [hasLowerCase, setHasLowerCase] = useState<boolean>(false)
  const [hasNumber, setHasNumber] = useState<boolean>(false)

  const handleAccessCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccessCode(e.target.value)
  }

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    const isValidLength = val.length >= 8
    const upperCase = /[A-Z]/.test(val)
    const lowerCase = /[a-z]/.test(val)
    const number = /[0-9]/.test(val)

    setHasMinLength(isValidLength)
    setHasUpperCase(upperCase)
    setHasLowerCase(lowerCase)
    setHasNumber(number)
    setValidPassword(isValidLength && upperCase && lowerCase && number)
    setMatchingVerify(val === newVerify && val.length > 0)
    setNewPassword(val)
  }

  const handleVerifyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMatchingVerify(e.target.value === newPassword && e.target.value.length > 0)
    setNewVerify(e.target.value)
  }

  const handleResetPassword = () => {
    if (validPassword && matchingVerify && accessCode) {
      confirmResetPasswrd(accessCode, newPassword)
    }
  }

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2">
      {met ? (
        <Check className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <X className="w-3.5 h-3.5 text-gray-500" />
      )}
      <span className={`text-xs ${met ? 'text-green-400' : 'text-gray-500'}`}>{text}</span>
    </div>
  )

  return (
    <div className="min-h-screen w-full bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl -translate-y-1/2 animate-float" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent-gold/15 rounded-full blur-3xl translate-x-1/2 animate-float-slow" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-pink/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 animate-float-slower" />

      {/* Main card */}
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo and header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-dark-card rounded-2xl border border-dark-border mb-4 animate-bounce-subtle">
            <img className="h-14 w-14 object-contain" src={logo} alt="Logo" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Reset Password</h1>
          <p className="text-gray-400">Create a new secure password</p>
        </div>

        {/* Reset password form card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-8 shadow-2xl animate-scale-in">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-accent-purple/20 to-accent-gold/20 rounded-2xl flex items-center justify-center border border-dark-border animate-pulse-slow">
              <Shield className="w-8 h-8 text-accent-teal" />
            </div>
          </div>

          <DarkAuthInput
            value={accessCode}
            handleFunction={handleAccessCodeChange}
            placeholder="Enter reset code from email"
            type="text"
            icon="password"
          />

          <DarkAuthInput
            value={newPassword}
            handleFunction={handleNewPasswordChange}
            placeholder="New password"
            type={showPassword ? 'text' : 'password'}
            icon="password"
            showPasswordToggle={true}
            onTogglePassword={() => setShowPassword(!showPassword)}
            isPasswordVisible={showPassword}
          />

          {/* Password requirements */}
          {newPassword && (
            <div className="mb-4 p-3 bg-dark-input rounded-xl border border-dark-border">
              <p className="text-xs text-gray-400 mb-2">Password requirements:</p>
              <div className="space-y-1">
                <RequirementItem met={hasMinLength} text="At least 8 characters" />
                <RequirementItem met={hasUpperCase} text="One uppercase letter" />
                <RequirementItem met={hasLowerCase} text="One lowercase letter" />
                <RequirementItem met={hasNumber} text="One number" />
              </div>
            </div>
          )}

          <DarkAuthInput
            value={newVerify}
            handleFunction={handleVerifyChange}
            placeholder="Confirm new password"
            type={showVerifyPassword ? 'text' : 'password'}
            icon="password"
            showPasswordToggle={true}
            onTogglePassword={() => setShowVerifyPassword(!showVerifyPassword)}
            isPasswordVisible={showVerifyPassword}
            error={newVerify && !matchingVerify ? "Passwords don't match" : undefined}
          />

          {resetError && (
            <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm">{resetError}</p>
            </div>
          )}

          <button
            onClick={handleResetPassword}
            disabled={!validPassword || !matchingVerify || !accessCode}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-accent-teal to-accent-deep-purple
                       text-white font-semibold rounded-xl
                       hover:opacity-90 hover:scale-[1.02] transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-lg shadow-accent-teal/25 hover:shadow-xl hover:shadow-accent-teal/30 mt-2"
          >
            Reset Password
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-dark-border" />
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-dark-border" />
          </div>

          {/* Back to login link */}
          <Link to="/" className="block text-center">
            <span className="text-accent-gold hover:text-accent-gold-light transition-colors text-sm font-medium">
              Back to Login
            </span>
          </Link>
        </div>

        {/* Security note */}
        <div className="mt-6 flex justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-dark-card/50 rounded-xl px-5 py-2.5 border border-dark-border hover:border-accent-teal/30 transition-colors duration-300">
            <p className="text-gray-500 text-xs text-center flex items-center gap-2">
              <svg className="w-4 h-4 text-accent-teal" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Your new password will be encrypted
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmResetPassword
